import React, { useState, useEffect, useRef } from 'react';
import {
  Paper,
  Typography,
  TextField,
  Button,
  Box,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Divider,
  Card,
  CardContent,
  CardActions,
  Chip,
  CircularProgress,
  Tabs,
  Tab,
  FormControlLabel,
  Switch
} from '@mui/material';
import { Save, Upload, CloudUpload, RestartAlt, Refresh, Psychology } from '@mui/icons-material';
import { ConfigService } from '../services/ConfigService';
import { ServerService } from '../services/ServerService';
import LiveLogsPanel from './LiveLogsPanel';
import GmailSetup from './GmailSetup';

interface Config {
  ai_provider: string;
  ollama_url: string;
  ollama_model: string;
  external_ai_api_key: string;
  external_ai_base_url: string;
  external_ai_model: string;
  gmail_credentials: string;
  target_email: string;
  secondary_target_email: string;
  server_port: string;
  enable_advertising_filter: string;
  email_check_interval: string;
  email_processing_enabled: string;
  ai_prompt: string;
}

const ConfigurationPanel: React.FC = () => {
  const [config, setConfig] = useState<Config>({
    ai_provider: 'ollama',
    ollama_url: 'http://192.168.111.41:11434',
    ollama_model: 'phi4-mini:latest',
    external_ai_api_key: '',
    external_ai_base_url: 'https://api.openai.com',
    external_ai_model: '',
    gmail_credentials: '',
    target_email: '',
    secondary_target_email: '',
    server_port: '3000',
    enable_advertising_filter: 'true',
    email_check_interval: '5',
    email_processing_enabled: 'true',
    ai_prompt: ''
  });

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [restarting, setRestarting] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error', text: string } | null>(null);
  const [currentTab, setCurrentTab] = useState(0);
  const [ollamaModels, setOllamaModels] = useState<string[]>([]);
  const [loadingModels, setLoadingModels] = useState(false);
  const [externalAIModels, setExternalAIModels] = useState<string[]>([]);
  const [loadingExternalModels, setLoadingExternalModels] = useState(false);
  const [testingExternalAI, setTestingExternalAI] = useState(false);
  const [externalAITestResult, setExternalAITestResult] = useState<{ type: 'success' | 'error', text: string } | null>(null);
  const promptTextAreaRef = useRef<HTMLTextAreaElement>(null);

  const suggestedExternalAiModels = [
    'gpt-4o',
    'gpt-4o-mini',
    'gpt-4-turbo',
    'gpt-4',
    'gpt-3.5-turbo',
    'llama-3.3-70b-versatile',
    'mistral-large-latest',
    'deepseek-chat'
  ];

  useEffect(() => {
    loadConfig();
  }, []);

  const loadOllamaModels = async () => {
    if (!config.ollama_url) return;
    
    setLoadingModels(true);
    try {
      const response = await fetch('/api/logs/ollama-models', {
        credentials: 'include'
      });
      
      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setOllamaModels(data.models);
          setMessage({ type: 'success', text: `Found ${data.models.length} Ollama models` });
        } else {
          setMessage({ type: 'error', text: 'Failed to load models: ' + data.error });
        }
      } else {
        setMessage({ type: 'error', text: 'Failed to connect to Ollama server' });
      }
    } catch (error) {
      console.error('Failed to load Ollama models:', error);
      setMessage({ type: 'error', text: 'Error loading Ollama models: ' + (error as Error).message });
    } finally {
      setLoadingModels(false);
    }
  };

  const loadConfig = async () => {
    try {
      const response = await ConfigService.getConfig();
      setConfig(response.data);
      // Auto-resize prompt field after loading
      setTimeout(() => {
        if (promptTextAreaRef.current) {
          adjustTextAreaHeight();
        }
      }, 100);
    } catch (error) {
      console.error('Failed to load config:', error);
      setMessage({ type: 'error', text: 'Failed to load configuration' });
    } finally {
      setLoading(false);
    }
  };

  const adjustTextAreaHeight = () => {
    const textArea = promptTextAreaRef.current;
    if (textArea) {
      // Reset height to measure scrollHeight
      textArea.style.height = 'auto';
      // Set height to fit content, with minimum and maximum
      const minHeight = 200;
      const maxHeight = 600;
      const scrollHeight = Math.max(minHeight, Math.min(maxHeight, textArea.scrollHeight + 20));
      textArea.style.height = scrollHeight + 'px';
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setMessage(null);
    
    try {
      await ConfigService.updateConfig(config);
      setMessage({ type: 'success', text: 'Configuration saved successfully' });
    } catch (error) {
      console.error('Failed to save config:', error);
      setMessage({ type: 'error', text: 'Failed to save configuration' });
    } finally {
      setSaving(false);
    }
  };

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setUploading(true);
    setMessage(null);

    try {
      await ConfigService.uploadGmailCredentials(file);
      setMessage({ type: 'success', text: 'Gmail credentials uploaded successfully' });
      loadConfig();
    } catch (error) {
      console.error('Failed to upload credentials:', error);
      setMessage({ type: 'error', text: 'Failed to upload Gmail credentials' });
    } finally {
      setUploading(false);
    }
  };

  const handleConfigChange = (field: keyof Config, value: string) => {
    setConfig(prev => ({ ...prev, [field]: value }));
  };

  const loadExternalAIModels = async () => {
    setLoadingExternalModels(true);
    setExternalAITestResult(null);
    try {
      const response = await ConfigService.getExternalAIModels();
      if (response.data.success) {
        setExternalAIModels(response.data.models);
        setExternalAITestResult({ type: 'success', text: `${response.data.models.length} Modelle geladen` });
      } else {
        setExternalAITestResult({ type: 'error', text: 'Fehler: ' + response.data.error });
      }
    } catch (error) {
      setExternalAITestResult({ type: 'error', text: 'Verbindung fehlgeschlagen' });
    } finally {
      setLoadingExternalModels(false);
    }
  };

  const handleTestExternalAI = async () => {
    setTestingExternalAI(true);
    setExternalAITestResult(null);
    try {
      const response = await ConfigService.testExternalAI(
        config.external_ai_base_url,
        config.external_ai_api_key,
        config.external_ai_model
      );
      if (response.data.success) {
        setExternalAITestResult({ type: 'success', text: response.data.message });
      } else {
        setExternalAITestResult({ type: 'error', text: response.data.error });
      }
    } catch (error) {
      setExternalAITestResult({ type: 'error', text: 'Test fehlgeschlagen' });
    } finally {
      setTestingExternalAI(false);
    }
  };

  const handleServerRestart = async () => {
    setRestarting(true);
    setMessage(null);

    try {
      await ServerService.restart();
      setMessage({ type: 'success', text: 'Server is restarting... Please wait a moment and refresh the page.' });
    } catch (error) {
      console.error('Failed to restart server:', error);
      setMessage({ type: 'error', text: 'Failed to restart server' });
    } finally {
      setRestarting(false);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress />
      </Box>
    );
  }

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setCurrentTab(newValue);
  };

  return (
    <Box>
      <Typography variant="h4" component="h1" gutterBottom>
        Email Assistant Configuration
      </Typography>

      {message && (
        <Alert severity={message.type} sx={{ mb: 2 }}>
          {message.text}
        </Alert>
      )}

      <Tabs value={currentTab} onChange={handleTabChange} sx={{ mb: 3 }}>
        <Tab label="Configuration" />
        <Tab label="Gmail Setup" />
        <Tab label="Live Logs & Testing" />
      </Tabs>

      {currentTab === 0 && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 3 }}>
          <Card sx={{ flex: 1 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                AI Configuration
              </Typography>
              
              <FormControl fullWidth margin="normal">
                <InputLabel>AI Provider</InputLabel>
                <Select
                  value={config.ai_provider}
                  onChange={(e) => handleConfigChange('ai_provider', e.target.value)}
                >
                  <MenuItem value="ollama">Ollama (Local)</MenuItem>
                  <MenuItem value="external">External API</MenuItem>
                  <MenuItem value="fallback">Fallback Only (No AI)</MenuItem>
                </Select>
              </FormControl>

              {config.ai_provider === 'ollama' && (
                <>
                  <TextField
                    fullWidth
                    label="Ollama URL"
                    value={config.ollama_url}
                    onChange={(e) => handleConfigChange('ollama_url', e.target.value)}
                    margin="normal"
                    placeholder="http://192.168.111.41:11434"
                  />
                  <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                    <FormControl fullWidth margin="normal">
                      <InputLabel>Ollama Model</InputLabel>
                      <Select
                        value={config.ollama_model}
                        onChange={(e) => handleConfigChange('ollama_model', e.target.value)}
                      >
                        {ollamaModels.map(model => (
                          <MenuItem key={model} value={model}>{model}</MenuItem>
                        ))}
                        {ollamaModels.length === 0 && (
                          <MenuItem value={config.ollama_model}>{config.ollama_model}</MenuItem>
                        )}
                      </Select>
                    </FormControl>
                    <Button
                      variant="outlined"
                      onClick={loadOllamaModels}
                      disabled={loadingModels}
                      startIcon={loadingModels ? <CircularProgress size={16} /> : <Refresh />}
                      sx={{ minWidth: 120 }}
                    >
                      {loadingModels ? 'Loading...' : 'Refresh'}
                    </Button>
                  </Box>
                </>
              )}

              {config.ai_provider === 'external' && (
                <>
                  <TextField
                    fullWidth
                    label="Base URL"
                    value={config.external_ai_base_url}
                    onChange={(e) => handleConfigChange('external_ai_base_url', e.target.value)}
                    margin="normal"
                    placeholder="https://api.openai.com"
                    helperText="OpenAI-kompatibler Endpunkt — ohne /v1 (z.B. http://192.168.1.10:8001)"
                  />
                  <TextField
                    fullWidth
                    label="API Key"
                    type="password"
                    value={config.external_ai_api_key}
                    onChange={(e) => handleConfigChange('external_ai_api_key', e.target.value)}
                    margin="normal"
                    placeholder="sk-... (leer lassen für lokale Server ohne Auth)"
                  />
                  <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start', mt: 1 }}>
                    <FormControl fullWidth margin="normal" sx={{ mt: 0 }}>
                      <InputLabel>Modell</InputLabel>
                      <Select
                        value={config.external_ai_model}
                        onChange={(e) => handleConfigChange('external_ai_model', e.target.value)}
                        label="Modell"
                      >
                        {externalAIModels.length === 0 && config.external_ai_model && (
                          <MenuItem value={config.external_ai_model}>{config.external_ai_model}</MenuItem>
                        )}
                        {externalAIModels.map(m => (
                          <MenuItem key={m} value={m}>{m}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    <Button
                      variant="outlined"
                      onClick={loadExternalAIModels}
                      disabled={loadingExternalModels}
                      startIcon={loadingExternalModels ? <CircularProgress size={16} /> : <Refresh />}
                      sx={{ minWidth: 130, mt: 0, height: 56 }}
                    >
                      {loadingExternalModels ? 'Laden...' : 'Modelle laden'}
                    </Button>
                  </Box>
                  <Box sx={{ mt: 2 }}>
                    <Button
                      variant="outlined"
                      color="info"
                      onClick={handleTestExternalAI}
                      disabled={testingExternalAI || !config.external_ai_model}
                      startIcon={testingExternalAI ? <CircularProgress size={16} /> : <Psychology />}
                    >
                      {testingExternalAI ? 'Teste...' : 'Verbindung testen'}
                    </Button>
                  </Box>
                  {externalAITestResult && (
                    <Alert severity={externalAITestResult.type} sx={{ mt: 1 }}>
                      {externalAITestResult.text}
                    </Alert>
                  )}
                </>
              )}
            </CardContent>
          </Card>

            <Card sx={{ flex: 1 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Gmail Configuration
                </Typography>
              
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Upload Gmail API credentials JSON file
                </Typography>
                <input
                  accept=".json"
                  style={{ display: 'none' }}
                  id="gmail-credentials-upload"
                  type="file"
                  onChange={handleFileUpload}
                />
                <label htmlFor="gmail-credentials-upload">
                  <Button
                    variant="outlined"
                    component="span"
                    startIcon={uploading ? <CircularProgress size={16} /> : <CloudUpload />}
                    disabled={uploading}
                  >
                    {uploading ? 'Uploading...' : 'Upload Credentials'}
                  </Button>
                </label>
                {config.gmail_credentials && (
                  <Chip
                    label="Credentials configured"
                    color="success"
                    size="small"
                    sx={{ ml: 1 }}
                  />
                )}
              </Box>

              <TextField
                fullWidth
                label="Primary Target Email (Invoices)"
                value={config.target_email}
                onChange={(e) => handleConfigChange('target_email', e.target.value)}
                margin="normal"
                placeholder="invoices@company.com"
                helperText="Email address to forward detected invoices/bills to"
              />
              
              <TextField
                fullWidth
                label="Secondary Target Email (Normal Emails)"
                value={config.secondary_target_email}
                onChange={(e) => handleConfigChange('secondary_target_email', e.target.value)}
                margin="normal"
                placeholder="normal@company.com"
                helperText="Email address to forward normal emails (personal correspondence, business communications) to"
              />
              </CardContent>
            </Card>
          </Box>

          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 3 }}>
            <Card sx={{ flex: 1 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Server Configuration
                </Typography>
              
              <TextField
                fullWidth
                label="Server Port"
                type="number"
                value={config.server_port}
                onChange={(e) => handleConfigChange('server_port', e.target.value)}
                margin="normal"
                placeholder="3000"
                helperText="Port number for the server (requires restart)"
                inputProps={{ min: 1, max: 65535 }}
              />
              
              <Box sx={{ mt: 2 }}>
                <Button
                  variant="outlined"
                  color="warning"
                  onClick={handleServerRestart}
                  disabled={restarting}
                  startIcon={restarting ? <CircularProgress size={16} /> : <RestartAlt />}
                >
                  {restarting ? 'Restarting...' : 'Restart Server'}
                </Button>
              </Box>
              </CardContent>
            </Card>
          
            <Card sx={{ flex: 1 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  System Status
                </Typography>
              
              <Box sx={{ mt: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  Current server port: {config.server_port}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Server accessible externally: ✓
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Email processing: Every 5 minutes
                </Typography>
              </Box>
              </CardContent>
            </Card>
          </Box>

          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Email Processing Control
              </Typography>
              
              <FormControlLabel
                control={
                  <Switch
                    checked={config.email_processing_enabled === 'true'}
                    onChange={(e) => handleConfigChange('email_processing_enabled', e.target.checked ? 'true' : 'false')}
                  />
                }
                label="Enable automatic email processing"
              />
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1, mb: 2 }}>
                When enabled, the system will automatically check and process emails at the configured interval
              </Typography>

              <TextField
                fullWidth
                label="Email Check Interval (minutes)"
                type="number"
                value={config.email_check_interval}
                onChange={(e) => handleConfigChange('email_check_interval', e.target.value)}
                margin="normal"
                placeholder="5"
                helperText="How often to check for new emails (minimum 1 minute)"
                inputProps={{ min: 1, max: 60 }}
              />

              <FormControlLabel
                control={
                  <Switch
                    checked={config.enable_advertising_filter === 'true'}
                    onChange={(e) => handleConfigChange('enable_advertising_filter', e.target.checked ? 'true' : 'false')}
                  />
                }
                label="Automatically mark advertising emails as read"
              />
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1, mb: 2 }}>
                When enabled, emails identified as advertising/marketing will be automatically marked as read
              </Typography>
            </CardContent>
          </Card>

          <Card>
            <CardContent>
            <Typography variant="h6" gutterBottom>
              AI Prompt Configuration
            </Typography>
            <TextField
              fullWidth
              label="AI Processing Prompt"
              value={config.ai_prompt}
              onChange={(e) => handleConfigChange('ai_prompt', e.target.value)}
              margin="normal"
              multiline
              rows={15}
              placeholder="Enter the prompt for AI email processing..."
              helperText="This prompt will be used to instruct the AI on how to process emails. Use Ctrl+A to select all text for easy editing."
              inputRef={promptTextAreaRef}
              sx={{
                '& .MuiInputBase-root': {
                  alignItems: 'flex-start'
                },
                '& textarea': {
                  resize: 'vertical',
                  fontFamily: 'monospace',
                  fontSize: '13px',
                  lineHeight: '1.4'
                }
              }}
            />
            </CardContent>
          </Card>

          <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
            <Button
              variant="contained"
              size="large"
              onClick={handleSave}
              disabled={saving}
              startIcon={saving ? <CircularProgress size={16} /> : <Save />}
            >
              {saving ? 'Saving...' : 'Save Configuration'}
            </Button>
          </Box>
        </Box>
      )}

      {currentTab === 1 && (
        <GmailSetup />
      )}

      {currentTab === 2 && (
        <LiveLogsPanel />
      )}
    </Box>
  );
};

export default ConfigurationPanel;